import { storeToRefs } from 'pinia';
import { watch } from 'vue';
import { defineNuxtPlugin } from '#app/nuxt';
import { useAuth } from '#layers/access/stores/auth';
import { useLicenseStore, useSystemLicenseStore } from '#layers/license/public';

/**
 * Bridges auth state into the auth-agnostic license stores (both: org-level
 * surfaces read the system-pinned one). Instantiation is deferred until the
 * first authenticated session so a logged-out load doesn't boot the license
 * (and transitive workspace) stores.
 */
export default defineNuxtPlugin((nuxtApp) => {
	const { loggedIn } = storeToRefs(useAuth());

	let license: ReturnType<typeof useLicenseStore> | null = null;
	let systemLicense: ReturnType<typeof useSystemLicenseStore> | null = null;

	watch(loggedIn, (value) => {
		// Logged out and never instantiated: nothing to disable.
		if (!value && !license && !systemLicense) return;

		if (!license || !systemLicense) {
			// A later auth transition runs after the plugin's injection scope has ended,
			// while the stores' setup still resolves system-backed RStore modules.
			nuxtApp.runWithContext(() => {
				license ??= useLicenseStore();
				systemLicense ??= useSystemLicenseStore();
			});
		}

		if (!license || !systemLicense) throw new Error('License stores failed to initialize');

		license.setEnabled(value);
		systemLicense.setEnabled(value);
	}, { immediate: true });
});
